// src/utils/completionEmail.js
//
// "Job completed" client notification.
//
// Flow, triggered from Mark Completed on every page:
//   1. The completion is written to Firestore FIRST (see markOccurrenceCompleted).
//      Whatever the user answers here, the job is already marked complete.
//   2. This dialog opens immediately and asks yes/no.
//   3. In parallel it calls sendJobCompletionEmail({ dryRun: true }) so the
//      dialog can show the ACTUAL resolved recipients before anyone commits.
//   4. Yes -> the same callable runs for real. No -> nothing is sent.
//
// Recipient rules live in the Cloud Function, not here, so the addresses shown
// in this dialog are the addresses that will genuinely be used.
//
// WHAT WAS COMPLETED
//   The body of this email used to be job.jobDescription, which is what the
//   client ASKED for, not what we did. On a job where the scope changed in the
//   field — and that is most of them — the client got told we did something we
//   did not do. The dialog now collects a real summary: tick the notes that
//   describe the work, type anything else, or both. The two are joined and
//   that text is what the client reads under "Work Performed".
//
// ATTACHMENTS
//   Photos on those notes, and the job's own uploaded files, can be picked and
//   sent with the email. Nothing is attached unless it is chosen - a client
//   should not receive an internal work order because it happened to be on the
//   job.

import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebaseConfig";

const functions = getFunctions(app, "us-central1");
const sendJobCompletionEmail = httpsCallable(functions, "sendJobCompletionEmail");

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 10090,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
};

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  padding: 24,
  width: "min(520px, 94vw)",
  maxHeight: "90vh",
  overflowY: "auto",
  boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
  fontFamily: "inherit",
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  gap: 14,
  padding: "7px 0",
  borderBottom: "1px solid #f1f5f9",
  fontSize: 14,
};

const labelStyle = { color: "#64748b", flexShrink: 0 };
const valueStyle = { fontWeight: 700, textAlign: "right", wordBreak: "break-word" };

const buttonBase = {
  padding: "11px 18px",
  borderRadius: 10,
  fontWeight: 800,
  cursor: "pointer",
  fontSize: 14,
};

const yesButton = {
  ...buttonBase,
  border: "1px solid #15803d",
  background: "#16a34a",
  color: "#fff",
};

const noButton = {
  ...buttonBase,
  border: "1px solid #94a3b8",
  background: "#f1f5f9",
  color: "#0f172a",
};

const CompletionEmailDialog = ({ job, occurrenceKey, onDecision }) => {
  const [lookup, setLookup] = useState({ state: "loading" });
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [picked, setPicked] = useState(() => new Set());
  const [pickedFiles, setPickedFiles] = useState(() => new Set());
  const [typed, setTyped] = useState("");

  const clientWorkOrder = String(job?.clientWorkOrder ?? "").trim();

  /* Notes live in TWO places and the branch that used to pick between them was
     wrong. Recurring jobs write to occurrenceNotes[date]; everything else
     writes to job.notes. But Home passes an occurrenceKey for every job, not
     just recurring ones, so a plain job with notes on job.notes was looked up
     in occurrenceNotes, came back empty, and the dialog said there were none.

     So read both and let each note's own occurrenceDate decide. A note with no
     occurrenceDate belongs to the job; one with a date belongs to that visit
     only, because telling a client about work done on a different visit would
     be worse than showing nothing. */
  const notes = React.useMemo(() => {
    const flat = Array.isArray(job?.notes) ? job.notes : [];
    const forOccurrence = occurrenceKey
      ? (job?.occurrenceNotes || {})[occurrenceKey] || []
      : [];

    const keep = flat.filter((n) => {
      const d = n?.occurrenceDate;
      return !d || !occurrenceKey || d === occurrenceKey;
    });

    /* Both stores can hold the same note on jobs that changed type. Keyed on
       text + timestamp because notes carry no id of their own. */
    const seen = new Set();
    return [...keep, ...forOccurrence]
      .filter((n) => {
        const text = String(n?.text || "").trim();
        if (!text) return false;
        const sig = `${text}__${n?.timestamp || ""}`;
        if (seen.has(sig)) return false;
        seen.add(sig);
        return true;
      })
      .map((n, i) => ({ ...n, i }));
  }, [job, occurrenceKey]);

  /* Everything on the job that could be sent along: files attached to the
     notes above, plus the job's own uploaded files, which are stored as two
     parallel arrays rather than a list of objects. */
  const files = React.useMemo(() => {
    const out = [];
    const seen = new Set();

    const push = (url, name, from) => {
      if (!url || seen.has(url)) return;
      seen.add(url);
      out.push({ url, name: name || "Attachment", from });
    };

    notes.forEach((n) => {
      (Array.isArray(n.files) ? n.files : []).forEach((f) => {
        push(
          f?.fileURL || f?.url || f?.downloadURL || "",
          f?.fileName || f?.name,
          "note"
        );
      });
    });

    const urls = Array.isArray(job?.fileURLs) ? job.fileURLs : [];
    const names = Array.isArray(job?.fileNames) ? job.fileNames : [];
    urls.forEach((url, i) => push(url, names[i], "job"));

    return out;
  }, [notes, job]);

  const toggleFile = (url) =>
    setPickedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });

  const toggle = (i) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  /* Ticked notes first, in the order they were written, then anything typed.
     Built fresh on every render rather than mirrored into the textarea, so
     ticking a note can never wipe something already typed. */
  const summary = React.useMemo(() => {
    const parts = notes
      .filter((n) => picked.has(n.i))
      .map((n) => String(n.text).trim());
    const tail = typed.trim();
    if (tail) parts.push(tail);
    return parts.join("\n\n");
  }, [notes, picked, typed]);

  // Resolve the real recipients while the user is reading the dialog.
  useEffect(() => {
    let cancelled = false;

    sendJobCompletionEmail({
      jobId: job?.id,
      occurrenceKey: occurrenceKey || "",
      dryRun: true,
    })
      .then((res) => {
        if (cancelled) return;
        setLookup({ state: "ready", ...(res?.data || {}) });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Completion email recipient lookup failed:", err);
        setLookup({
          state: "error",
          message:
            err?.message ||
            "Could not work out who to email for this property.",
        });
      });

    return () => {
      cancelled = true;
    };
  }, [job?.id, occurrenceKey]);

  const send = async () => {
    setSending(true);
    setError("");

    try {
      const res = await sendJobCompletionEmail({
        jobId: job?.id,
        occurrenceKey: occurrenceKey || "",
        dryRun: false,
        completionSummary: summary,
        attachments: files
          .filter((f) => pickedFiles.has(f.url))
          .map((f) => ({ url: f.url, name: f.name })),
      });

      onDecision({ sent: true, ...(res?.data || {}) });
    } catch (err) {
      console.error("Completion email failed:", err);
      setSending(false);
      setError(
        err?.message || "The email could not be sent. The job is still marked completed."
      );
    }
  };

  const recipients = [
    ...(lookup.to || []),
    ...(lookup.cc || []).map((address) => `${address} (cc)`),
  ];

  /* A summary is required. The old behaviour silently fell back to the job
     description, which is the bug this dialog exists to fix - better to stop
     here for a moment than to send the client the wrong story. */
  const canSend =
    lookup.state === "ready" && recipients.length > 0 && !sending && Boolean(summary);

  return (
    <div style={overlayStyle} onClick={() => !sending && onDecision({ sent: false })}>
      <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginTop: 0, marginBottom: 6 }}>
          Send completion notice to the client?
        </h3>

        <p style={{ margin: "0 0 16px", color: "#64748b", fontSize: 14, lineHeight: 1.5 }}>
          The job is already marked completed. This only decides whether an email
          goes out.
        </p>

        <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: "4px 14px 10px" }}>
          <div style={rowStyle}>
            <span style={labelStyle}>WO #</span>
            <span style={valueStyle}>{job?.wo || job?.id || "—"}</span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Client WO #</span>
            <span style={{ ...valueStyle, color: clientWorkOrder ? "#0f172a" : "#b45309" }}>
              {clientWorkOrder || "none on this job"}
            </span>
          </div>

          <div style={rowStyle}>
            <span style={labelStyle}>Property</span>
            <span style={valueStyle}>{job?.siteName || "—"}</span>
          </div>

          {occurrenceKey && (
            <div style={rowStyle}>
              <span style={labelStyle}>Service date</span>
              <span style={valueStyle}>{occurrenceKey}</span>
            </div>
          )}

          <div style={{ ...rowStyle, borderBottom: "none", alignItems: "flex-start" }}>
            <span style={labelStyle}>Goes to</span>
            <span style={valueStyle}>
              {lookup.state === "loading" && (
                <span style={{ fontWeight: 400, color: "#64748b" }}>
                  Looking up recipients…
                </span>
              )}

              {lookup.state === "error" && (
                <span style={{ color: "#b91c1c" }}>{lookup.message}</span>
              )}

              {lookup.state === "ready" &&
                (recipients.length ? (
                  recipients.map((address) => (
                    <div key={address} style={{ marginBottom: 2 }}>
                      {address}
                    </div>
                  ))
                ) : (
                  <span style={{ color: "#b91c1c" }}>
                    No manager or client email on this property
                  </span>
                ))}
            </span>
          </div>
        </div>

        <div style={{ marginTop: 18 }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
            What was completed?
          </div>
          <p style={{ margin: "0 0 10px", color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
            This is what the client reads. Check off any notes that describe the
            work, type anything else, or both.
          </p>

          {notes.length > 0 ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                maxHeight: 200,
                overflowY: "auto",
                marginBottom: 10,
              }}
            >
              {notes.map((n) => (
                <label
                  key={n.i}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "flex-start",
                    padding: "6px 10px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    background: picked.has(n.i) ? "#f0fdf4" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={picked.has(n.i)}
                    onChange={() => toggle(n.i)}
                    /* Sized explicitly. Left alone, the browser renders this
                       around 16px with its own margins, which on a row this
                       tight eats the width the text needs. */
                    style={{
                      width: 13,
                      height: 13,
                      margin: "3px 0 0",
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                  <span
                    /* flex:1 with minWidth:0 is what makes the text wrap
                       inside the row instead of refusing to shrink and
                       pushing the layout around. A flex item defaults to
                       min-width:auto, which will not go narrower than its
                       longest word. That is the jumbling. */
                    style={{ fontSize: 13, lineHeight: 1.4, flex: 1, minWidth: 0 }}
                  >
                    <span style={{ whiteSpace: "pre-line", overflowWrap: "anywhere" }}>
                      {n.text}
                    </span>
                    <span style={{ display: "block", color: "#94a3b8", fontSize: 11, marginTop: 2 }}>
                      {[n.author, n.timestamp].filter(Boolean).join(" \u00b7 ")}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p style={{ margin: "0 0 10px", fontSize: 13, color: "#b45309" }}>
              {occurrenceKey
                ? "No notes on this job for this service date. Type what was done below."
                : "No notes on this job yet. Type what was done below."}
            </p>
          )}

          <textarea
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="Type what was completed\u2026"
            rows={3}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "9px 11px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              fontSize: 13.5,
              fontFamily: "inherit",
              color: "#0f172a",
              background: "#fff",
              resize: "vertical",
            }}
          />

          {/* Shown because the message is assembled from two places. Without it
              nobody can tell what actually goes out until it has gone out. */}
          {summary ? (
            <div
              style={{
                marginTop: 10,
                padding: "9px 11px",
                background: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: 8,
                fontSize: 12.5,
                color: "#334155",
                whiteSpace: "pre-line",
                lineHeight: 1.5,
              }}
            >
              <span style={{ display: "block", color: "#64748b", fontWeight: 700, marginBottom: 4 }}>
                The client will see:
              </span>
              {summary}
            </div>
          ) : (
            <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#b45309" }}>
              Check off a note or type a message before sending.
            </p>
          )}
        </div>

        {files.length > 0 && (
          <div style={{ marginTop: 18 }}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>
              Attach files?
            </div>
            <p style={{ margin: "0 0 10px", color: "#64748b", fontSize: 13, lineHeight: 1.5 }}>
              Nothing is attached unless you check it.
            </p>

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 10,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              {files.map((f) => (
                <label
                  key={f.url}
                  style={{
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                    padding: "6px 10px",
                    borderBottom: "1px solid #f1f5f9",
                    cursor: "pointer",
                    background: pickedFiles.has(f.url) ? "#f0fdf4" : "transparent",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={pickedFiles.has(f.url)}
                    onChange={() => toggleFile(f.url)}
                    style={{
                      width: 13,
                      height: 13,
                      margin: 0,
                      flexShrink: 0,
                      cursor: "pointer",
                    }}
                  />
                  <span
                    style={{
                      fontSize: 13,
                      lineHeight: 1.4,
                      flex: 1,
                      minWidth: 0,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {f.name}
                  </span>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.03em",
                      textTransform: "uppercase",
                      color: "#94a3b8",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {f.from === "note" ? "from note" : "job file"}
                  </span>
                </label>
              ))}
            </div>

            {pickedFiles.size > 0 && (
              <p style={{ margin: "8px 0 0", fontSize: 12.5, color: "#0f766e", fontWeight: 700 }}>
                {pickedFiles.size} file{pickedFiles.size === 1 ? "" : "s"} will be attached.
              </p>
            )}
          </div>
        )}

        {lookup.state === "ready" && lookup.routing && (
          <p style={{ margin: "10px 0 0", fontSize: 12.5, color: "#64748b", lineHeight: 1.5 }}>
            {lookup.routing}
          </p>
        )}

        {lookup.state === "ready" && lookup.alreadySent && (
          <p style={{ margin: "10px 0 0", fontSize: 13, color: "#b45309", fontWeight: 700 }}>
            A completion email was already sent for this one on{" "}
            {lookup.alreadySent}. Sending again will send a duplicate.
          </p>
        )}

        {error && (
          <p style={{ margin: "12px 0 0", fontSize: 13, color: "#b91c1c", fontWeight: 700 }}>
            {error}
          </p>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 20, justifyContent: "flex-end" }}>
          <button
            style={noButton}
            disabled={sending}
            onClick={() => onDecision({ sent: false })}
          >
            No, don't send
          </button>

          <button
            style={{ ...yesButton, opacity: canSend ? 1 : 0.5, cursor: canSend ? "pointer" : "not-allowed" }}
            disabled={!canSend}
            onClick={send}
          >
            {sending ? "Sending…" : "Yes, send it"}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Opens the yes/no dialog and resolves once the user decides.
 * Never throws - a failed email must not roll back a completed job.
 *
 * Resolves: { sent: boolean, to?: string[], cc?: string[] }
 */
export const confirmAndSendCompletionEmail = (job, { occurrenceKey = "" } = {}) =>
  new Promise((resolve) => {
    if (!job?.id) {
      resolve({ sent: false, reason: "no job id" });
      return;
    }

    const container = document.createElement("div");
    document.body.appendChild(container);
    const root = createRoot(container);

    const finish = (result) => {
      root.unmount();
      container.remove();
      resolve(result || { sent: false });
    };

    root.render(
      <CompletionEmailDialog
        job={job}
        occurrenceKey={occurrenceKey}
        onDecision={finish}
      />
    );
  });

/**
 * Fire the email with no dialog. Used by the Completion Board's manual
 * "Email client" button, where the row itself is the confirmation.
 */
export const sendCompletionEmailNow = async (jobId, occurrenceKey = "", completionSummary = "") => {
  const res = await sendJobCompletionEmail({
    jobId,
    occurrenceKey,
    dryRun: false,
    /* Optional. Omitted entirely when blank so the function falls back to its
       old behaviour rather than sending an email with an empty body. */
    ...(completionSummary ? { completionSummary } : {}),
  });

  return res?.data || {};
};

export const previewCompletionEmailRecipients = async (jobId, occurrenceKey = "") => {
  const res = await sendJobCompletionEmail({
    jobId,
    occurrenceKey,
    dryRun: true,
  });

  return res?.data || {};
};
